<template>
  <div>
    <b-card>
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
        <div>
          <b-link class="d-inline-flex align-items-center text-muted mb-50"
            :to="{ name: 'client-members-page' }">
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
            <span>{{ $t('Back') }}</span>
          </b-link>
          <h4 class="mb-0">{{ $t('Add User') }}</h4>
        </div>
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <b-col md="6">
              <validation-provider #default="validationContext" name="First Name" rules="required">
                <b-form-group :label="$t('First Name')" label-for="first-name">
                  <b-form-input id="first-name" v-model="userData.firstname" autofocus
                    :state="getValidationState(validationContext)" trim placeholder="John" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider #default="validationContext" name="Last Name" rules="required">
                <b-form-group :label="$t('Last Name')" label-for="last-name">
                  <b-form-input id="last-name" v-model="userData.lastname"
                    :state="getValidationState(validationContext)" trim placeholder="Doe" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider #default="validationContext" name="User Name" rules="required">
                <b-form-group :label="$t('User Name')" label-for="username">
                  <b-form-input id="username" v-model="userData.email"
                    :state="getValidationState(validationContext)" trim />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider #default="validationContext" name="Password" rules="required">
                <b-form-group :label="$t('Password')" label-for="password">
                  <b-form-input id="password" v-model="userData.password"
                    :state="getValidationState(validationContext)" trim />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider #default="validationContext" name="Email" rules="email">
                <b-form-group :label="$t('User Email')" label-for="notification-email">
                  <b-form-input id="notification-email" v-model="userData.notificationEmail"
                    :state="getValidationState(validationContext)" trim />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col v-if="hasAccessSupervisorEmail" md="6">
              <validation-provider #default="validationContext" name="SupervisorEmail" rules="email">
                <b-form-group :label="$t('Supervisor Email')" label-for="supervisor-email">
                  <b-form-input id="supervisor-email" v-model="userData.supervisorEmail"
                    :state="getValidationState(validationContext)" trim />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider #default="validationContext" name="Mobile" rules="">
                <b-form-group :label="$t('Mobile')" label-for="mobile">
                  <b-form-input id="mobile" v-model="userData.mobile" type="number"
                    :state="getValidationState(validationContext)" trim placeholder="5554443322" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <b-form-group :label="$t('Select Department')" label-for="department-id">
                <department-picker :initialDepartment="null" @selectionChanged="onDepartmentSelectionChanged" />
                <validation-provider ref="departmentProvider" v-slot="ctx" :name="$t('Select Department')"
                  rules="required" slim>
                  <div>
                    <input v-model="departmentValidationValue" type="hidden" />
                    <div class="invalid-feedback" :class="{ 'd-block': ctx.errors[0] }">
                      {{ ctx.errors[0] }}
                    </div>
                  </div>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col v-if="isTurkeyClient" md="6">
              <b-form-group :label="$t('Select Title')" label-for="title-id">
                <title-picker @selectionChanged="onTitleSelectionChanged" />
                <validation-provider ref="titleProvider" v-slot="ctx" :name="$t('Select Title')" rules="required"
                  slim>
                  <div>
                    <input v-model="titleValidationValue" type="hidden" />
                    <div class="invalid-feedback" :class="{ 'd-block': ctx.errors[0] }">
                      {{ ctx.errors[0] }}
                    </div>
                  </div>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-checkbox v-model="wantsLdapLogin" class="mb-1">
            {{ $t('Check this box if you want the user to log in via LDAP') }}
          </b-form-checkbox>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2" type="submit"
              :disabled="submitting">
              <b-spinner v-if="submitting" small class="mr-50" />
              {{ $t('Add') }}
            </b-button>
            <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary"
              :to="{ name: 'client-members-page' }">
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BFormInvalidFeedback, BButton, BLink, BSpinner,
  BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, computed } from '@vue/composition-api'
import { required, email } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import router from '@/router'
import DepartmentPicker from '@/views/department/components/DepartmentPicker.vue'
import TitlePicker from '@/views/members/component/MemberTitlePicker.vue'
import memberStoreModule from '../memberStoreModule'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MEMBERS_MODULE = 'apps-members'

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BLink,
    BSpinner,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    DepartmentPicker,
    TitlePicker,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      email,
      submitting: false,
      selectedDepartment: null,
      selectedTitle: null,
      departmentValidationValue: '',
      titleValidationValue: '',
      wantsLdapLogin: false,
    }
  },
  created() {
    if (!store.hasModule(MEMBERS_MODULE)) store.registerModule(MEMBERS_MODULE, memberStoreModule)
  },
  methods: {
    onDepartmentSelectionChanged(payload) {
      this.selectedDepartment = payload
      this.departmentValidationValue = payload && payload.id != null ? String(payload.id) : ''
      this.$nextTick(() => {
        if (this.$refs.departmentProvider) {
          this.$refs.departmentProvider.validate(this.departmentValidationValue)
        }
      })
    },
    onTitleSelectionChanged(payload) {
      this.selectedTitle = payload
      this.titleValidationValue = payload ? (payload.title || payload) : ''
      this.$nextTick(() => {
        if (this.$refs.titleProvider) {
          this.$refs.titleProvider.validate(this.titleValidationValue)
        }
      })
    },
    onSubmit() {
      this.submitting = true
      this.userData.fullname = `${this.userData.firstname} ${this.userData.lastname}`

      if (this.selectedDepartment) {
        this.userData.platformdepartmentname = this.selectedDepartment.name
        this.userData.platformdepartmentid =
          this.selectedDepartment.departmentplatformid && this.selectedDepartment.departmentplatformid.length > 0
            ? this.selectedDepartment.departmentplatformid
            : this.selectedDepartment.id
      }

      if (this.isTurkeyClient && this.selectedTitle) {
        this.userData.platformtitlename = this.selectedTitle.title || this.selectedTitle
      } else {
        this.userData.platformtitlename = null
      }

      this.userData.isOutSource = this.wantsLdapLogin ? 0 : 1

      store.dispatch(`${MEMBERS_MODULE}/createNewMember`, this.userData)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('Success'),
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          router.push({ name: 'client-members-page' })
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('Error'),
              icon: 'AlertOctagonIcon',
              variant: 'warning',
              text: error && error.message,
            },
          })
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
  setup() {
    const blankUserData = {
      fullname: '',
      firstname: '',
      lastname: '',
      username: '',
      email: '',
      notificationEmail: '',
      password: '',
      company: '',
      country: '90',
      platformdepartmentid: null,
      platformdepartmentname: null,
      platformtitlename: null,
      isOutSource: 1,
    }

    const userData = ref(JSON.parse(JSON.stringify(blankUserData)))
    const resetUserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }

    const hasAccessSupervisorEmail = computed(() => {
      const client = localStorage.getItem('client')
      return client === 'brazil-client' || client === 'niso-client'
    })

    const isTurkeyClient = computed(() => localStorage.getItem('client') === 'turkey-client')

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetUserData)

    return {
      userData,
      hasAccessSupervisorEmail,
      isTurkeyClient,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
